import { Component, EventEmitter, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { OutreachLink } from '../../models/outreach-link';
import { takeUntil } from 'rxjs/operators';
import { PublicLinkService } from '../../services/public-link.service';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { CommonService } from '../../../../shared/services/common/common.service';

interface KeyValuePair {
  key: string;
  value: string;
}

@Component({
  selector: 'app-outreach-link-create',
  standalone: true,
  imports: [FormsModule, SvgIconComponent, SecondaryPopupComponent],
  templateUrl: './outreach-link-create.component.html',
  styleUrl: './outreach-link-create.component.scss',
})
export class OutreachLinkCreateComponent extends DestroyBase {
  private publicLinkService = inject(PublicLinkService);
  private auditLogService = inject(AuditLogService);
  private commonService = inject(CommonService);

  @Output() cancelled = new EventEmitter<void>();
  @Output() popupClosed = new EventEmitter<boolean>();
  @Output() linkGenerated = new EventEmitter<number>();

  // Form properties
  linkName = '';
  keyValuePairs: KeyValuePair[] = [{ key: '', value: '' }];
  submissionVisibility = 1;

  // Link generation state
  isGenerating = false;

  get isGenerateButtonEnabled(): boolean {
    return (
      this.linkName.trim().length > 0 &&
      !this.isGenerating &&
      this.submissionVisibility !== null
    );
  }

  get canAddNewKeyValuePair(): boolean {
    const lastPair = this.keyValuePairs[this.keyValuePairs.length - 1];
    return lastPair.key.trim() !== '' && lastPair.value.trim() !== '';
  }

  addKeyValuePair(): void {
    if (this.canAddNewKeyValuePair) {
      this.keyValuePairs.push({ key: '', value: '' });
    }
  }

  removeKeyValuePair(index: number): void {
    if (this.keyValuePairs.length > 1) {
      this.keyValuePairs.splice(index, 1);
    }
  }

  generateLink(): void {
    if (
      !this.isGenerateButtonEnabled ||
      this.isGenerating ||
      !this.submissionVisibility
    )
      return;

    this.auditLogService.logFeature({
      featureName: 'outreach_link',
      action: 'create',
      meta: {
        linkName: this.linkName,
      },
      description: 'User creates a new outreach link',
    });

    this.isGenerating = true;

    // Filter out empty key-value pairs
    const validPairs = this.keyValuePairs.filter(
      (pair) => pair.key.trim() !== '' && pair.value.trim() !== '',
    );

    // Create metadata object from key-value pairs
    const metadata: Record<string, string> = {};
    validPairs.forEach((pair) => {
      metadata[pair.key] = pair.value;
    });

    // Call the API to create the outreach link
    this.publicLinkService
      .createOutreachLink(this.linkName, metadata, this.submissionVisibility)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: OutreachLink) => {
          this.isGenerating = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Link generated successfully!',
          });

          // Emit the generated link ID to parent component
          this.linkGenerated.emit(response.id);
        },
        error: (error) => {
          console.error('Error creating outreach link:', error);
          this.isGenerating = false;
          // You might want to show an error message to the user here
        },
      });
  }

  closePopup(isClickedOutside: boolean): void {
    this.popupClosed.emit(isClickedOutside);
  }
}
